
import React, { useEffect } from 'react';
import { Card, CardContent } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { 
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage
} from "@/components/ui/form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import * as z from "zod";

// Form validation schema
const accountFormSchema = z.object({
  externalId: z.string().min(1, "External ID is required"),
  performanceUrl: z.string().url("Please enter a valid URL").or(z.string().length(0)),
  isPrimary: z.boolean().default(true),
  isActive: z.boolean().default(true),
  exchange: z.string().min(1, "Exchange is required"),
});

type AccountFormValues = z.infer<typeof accountFormSchema>;

interface AccountFormProps {
  onSubmit: (data: any, isValid: boolean) => void;
  initialData?: any;
}

export const AccountForm = ({ onSubmit, initialData }: AccountFormProps) => {
  // Create form with validation
  const form = useForm<AccountFormValues>({
    resolver: zodResolver(accountFormSchema),
    defaultValues: {
      externalId: initialData?.externalId || '',
      performanceUrl: initialData?.performanceUrl || '',
      isPrimary: initialData?.isPrimary !== false,
      isActive: initialData?.isActive !== false,
      exchange: initialData?.exchange || '',
    },
  });
  
  // Notify parent component about form validity
  useEffect(() => {
    const subscription = form.watch(() => {
      if (Object.keys(form.formState.touchedFields).length > 0) {
        const formData = form.getValues();
        onSubmit({
          account: formData
        }, form.formState.isValid);
      }
    });
    
    return () => subscription.unsubscribe();
  }, [form, onSubmit]);
  
  const handleFormSubmit = (data: AccountFormValues) => {
    onSubmit({
      account: data
    }, true);
  };

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(handleFormSubmit)}>
        <Card>
          <CardContent className="pt-6">
            <div className="space-y-4">
              <FormField
                control={form.control}
                name="externalId"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>External ID <span className="text-red-500">*</span></FormLabel>
                    <FormControl>
                      <Input {...field} />
                    </FormControl>
                    <FormDescription>
                      The unique identifier for this account in the external system
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />
              
              <FormField
                control={form.control}
                name="exchange"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Exchange <span className="text-red-500">*</span></FormLabel>
                    <FormControl>
                      <Select 
                        onValueChange={field.onChange} 
                        defaultValue={field.value}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select exchange" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="OKX">OKX</SelectItem>
                          <SelectItem value="Bybit">Bybit</SelectItem>
                          <SelectItem value="Bitget">Bitget</SelectItem>
                          <SelectItem value="Phemex">Phemex</SelectItem>
                        </SelectContent>
                      </Select>
                    </FormControl>
                    <FormDescription>
                      The exchange platform for this account
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />
              
              <FormField
                control={form.control}
                name="performanceUrl"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Performance URL (optional)</FormLabel>
                    <FormControl>
                      <Input {...field} type="url" />
                    </FormControl>
                    <FormDescription>
                      URL to view the account's performance data (if available)
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />
              
              <div className="flex flex-col space-y-4">
                <FormField
                  control={form.control}
                  name="isPrimary"
                  render={({ field }) => (
                    <FormItem className="flex items-center space-x-2">
                      <FormControl>
                        <Switch 
                          checked={field.value}
                          onCheckedChange={field.onChange}
                        />
                      </FormControl>
                      <div className="space-y-0.5">
                        <FormLabel>Primary Account</FormLabel>
                        <FormDescription>
                          Set as the primary account for this strategy
                        </FormDescription>
                      </div>
                    </FormItem>
                  )}
                />
                
                <FormField
                  control={form.control}
                  name="isActive"
                  render={({ field }) => (
                    <FormItem className="flex items-center space-x-2">
                      <FormControl>
                        <Switch 
                          checked={field.value}
                          onCheckedChange={field.onChange}
                        />
                      </FormControl>
                      <div className="space-y-0.5">
                        <FormLabel>Account Active</FormLabel>
                        <FormDescription>
                          Active accounts are visible and usable in the system
                        </FormDescription>
                      </div>
                    </FormItem>
                  )}
                />
              </div>
            </div>
          </CardContent>
        </Card>
      </form>
    </Form>
  );
};
